/**
 * FilterPanel — compact filter button with dropdown panel.
 * Shows active filter count as a badge on the button.
 */
import React, { useState, useRef, useEffect } from 'react';
import './FilterPanel.css';

export interface FilterConfig {
  key: string;
  label: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
}

interface FilterPanelProps {
  filters: FilterConfig[];
  onClearAll: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({ filters, onClearAll }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const activeCount = filters.filter(f => f.value !== '').length;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="fp-wrap" ref={ref}>
      <button
        className={`fp-btn ${activeCount > 0 ? 'fp-btn-active' : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Filters"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        Filters
        {activeCount > 0 && <span className="fp-badge">{activeCount}</span>}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 2 }}>
          <path d={open ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}/>
        </svg>
      </button>

      {open && (
        <div className="fp-panel">
          <div className="fp-panel-header">
            <span className="fp-panel-title">Filters</span>
            {activeCount > 0 && (
              <button className="fp-clear-all" onClick={() => { onClearAll(); setOpen(false); }}>
                Clear all
              </button>
            )}
          </div>
          <div className="fp-panel-body">
            {filters.map(f => (
              <div key={f.key} className="fp-filter-row">
                <label className="fp-filter-label">{f.label}</label>
                <select
                  className="fp-filter-select"
                  value={f.value}
                  onChange={e => f.onChange(e.target.value)}
                >
                  <option value="">All</option>
                  {f.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
